import { useState } from 'react';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface Props {
  value: string | null;
  onSave: (next: Date | null) => Promise<void>;
  editable: boolean;
}

export default function InlineDateCell({ value, onSave, editable }: Props) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const display = value ? (() => {
    try { return format(new Date(value), 'dd/MM/yyyy'); } catch { return '—'; }
  })() : '—';

  const handleSelect = async (d: Date | null) => {
    setSaving(true);
    setOpen(false);
    try {
      await onSave(d);
    } catch {
      // toast in mutation
    } finally {
      setSaving(false);
    }
  };

  if (saving) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>{display}</span>
      </div>
    );
  }

  if (!editable) {
    return <span className="text-sm text-muted-foreground px-2">{display}</span>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="text-sm text-left w-full px-2 py-1 rounded cursor-pointer hover:bg-muted/60"
        >
          {display}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" onClick={(e) => e.stopPropagation()}>
        <Calendar
          mode="single"
          selected={value ? new Date(value) : undefined}
          onSelect={(d) => d && handleSelect(d)}
          initialFocus
          className={cn('p-3 pointer-events-auto')}
        />
        {value && (
          <div className="border-t p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => handleSelect(null)}
            >
              Xóa ngày
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
